// 薬歴の基本型定義

import { PrescriptionData } from './yakurekiPrescription'
import { Soaps } from './yakurekiSoap'
import type { Highlight } from './highlight'

/**
 * 薬歴の状態（statusフィールド）
 * - pending: 録音待ち
 * - recording: 録音中 (streaming 経路で stream/start 〜 finalize 間)
 * - processing: 処理中
 * - completed: 完了
 * - copied: コピー済み
 * - dismissed: 対応完了（患者メニューからの消し込み）
 * - failed: 失敗（再生成可）
 * - failed_short: 失敗（短音声）
 */
export type YakurekiStatus =
  | 'pending'
  | 'recording'
  | 'processing'
  | 'completed'
  | 'copied'
  | 'dismissed'
  | 'failed'
  | 'failed_short'

// =============================================================================
// YakurekiStatus 分類ヘルパー
// =============================================================================
// 各画面が `status === 'processing'` 等の文字列リテラル比較を直接書くと、status
// が増えた時の漏れや、failed/failed_short の片方しか見ていない一貫性欠如が
// 起きやすい. 意味的なグループ判定はここに集約し、callsite はこれらを使う.
//
// 設計メモ: SOAP の表示判定では「生成中 (overlay)」「失敗 terminal (再生成 UI)」
// の 2 区分が頻出するため、まず以下 2 つを公開する. 「terminal vs non-terminal」
// 等は callsite が出てきたら追加する (YAGNI).

/**
 * SOAP 生成中とみなせる status か.
 * UI の "SOAP生成中" overlay や、refetchInterval の polling 判定に使う.
 */
export const isInProgressStatus = (status: YakurekiStatus): boolean =>
  status === 'processing'

/**
 * 文字起こし / SOAP 生成が失敗した terminal status か.
 * `failed` と `failed_short` を両方含む. ただし「再生成可否」は両者で異なる
 * (`failed_short` は再生成不可) ため、再生成 UI の出し分けは個別に
 * `status === 'failed'` で判定すること.
 */
export const isFailureStatus = (status: YakurekiStatus): boolean =>
  status === 'failed' || status === 'failed_short'

/**
 * SOAP 生成完了 + 薬剤師業務 phase の status か.
 * `completed` (生成済) / `copied` (薬歴貼付済) / `dismissed` (業務完結).
 * Pre/Post 画面切替の判定に使う (Post 画面は本判定 OR isFailureStatus で表示).
 */
export const isCompletedFamily = (status: YakurekiStatus): boolean =>
  status === 'completed' || status === 'copied' || status === 'dismissed'

/**
 * サイドバー患者リストの指導フェーズ 3 分割 (design-book 04-apps/kakeru.md).
 * - pre  (指導前): 未録音 (pending) / 短音声失敗で要再録音 (failed_short)
 * - post (指導済): 録音以降・薬歴コピー前 (recording / processing / completed / failed)
 * - done (完了)  : 薬歴コピー済 (copied) / 対応完了消し込み (dismissed)
 *
 * pre は担当薬剤師フィルタを適用しない (pharmacist_id は録音時に確定するため
 * pre は原則 null = フィルタで「これから対応すべき患者」が消えるのを防ぐ)。
 */
export type GuidancePhase = 'pre' | 'post' | 'done'

export const guidancePhaseOf = (status: YakurekiStatus): GuidancePhase => {
  if (status === 'pending' || status === 'failed_short') return 'pre'
  if (status === 'copied' || status === 'dismissed') return 'done'
  return 'post'
}

/**
 * 録音セッションの種別。
 * - walk_in: 来局 (外来)。医師宛の情報提供書は随時アクションで生成。
 * - home_visit: 在宅 (訪問)。訪問報告書 (訪問時状況 + 伝達事項) を派生生成する。
 * 録音→SOAP→薬歴の核は両者共通で、違いは「医師等への提出書類」だけ。
 * 録音開始後の切替は back が 409 で拒否する (PENDING の間のみ変更可)。
 */
export type SessionType = 'walk_in' | 'home_visit'

/**
 * 遠隔服薬指導の依頼の札 (back log/250)。状態名は back が時刻から決める。
 * direction は見ている店から見た向き (頼む店 = outgoing・遠隔の店 = incoming)。
 */
export type RemoteGuidanceState =
  | 'requested'
  | 'accepted'
  | 'waiting_patient'
  | 'seated'
  | 'in_guidance'
  | 'finished'
  | 'cancelled'
  | 'declined'

export type RemoteGuidanceSummary = {
  id: string
  state: RemoteGuidanceState
  /** 取り消し・お断り以外 (指導済を含む)。札の出し分けはこれと state だけで決める */
  is_live: boolean
  state_since: string | null
  direction: 'outgoing' | 'incoming'
  from_pharmacy_name: string
  to_pharmacy_name: string
  request_note: string
  decline_note: string
  handover_note: string
}

export type Yakureki = {
  id: string
  label: string
  queue_number?: string | null
  patient_name?: string | null
  created_at: string
  has_audio: boolean
  status: YakurekiStatus
  is_create_from_nsips: boolean
  pharmacist_id?: string | null
  pharmacist_name?: string | null
  remote?: RemoteGuidanceSummary | null
}

export type PatientGender = 'male' | 'female' | 'other'

/**
 * Transcription の文字起こし本体状態 (Pass 1)
 * - chirp-streaming: Chirp 3 StreamingRecognize で即時確定
 * - legacy-gemini-sync: Gemini 同期経路 (batch モード)
 * - gemini-2.5-flash / gemini-3-flash-preview: post_batch 完了後の上書き engine
 */
export type TranscriptionEngine =
  | 'chirp-streaming'
  | 'legacy-gemini-sync'
  | 'gemini-2.5-flash'
  | 'gemini-3-flash-preview'

export type TranscriptionStatus = 'pending' | 'running' | 'completed' | 'failed'

/**
 * Gemini Batch 後追い文字起こし (Pass 2) の状態
 * - not_applicable: legacy batch 経路 (enqueue されない)
 * - pending: streaming 経路で Pass 1 確定済、Gemini Batch submit 未実施
 * - running: Gemini Batch job 実行中
 * - completed: Pass 2 完了、transcript が話者分離付き utterances で上書き済
 * - failed: submit エラー / job 失敗 / 24h timeout
 */
export type TranscriptionPostBatchStatus =
  | 'not_applicable'
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'

/**
 * YakurekiDetail.transcription — 話者分離トグル enable 判定 + polling 終端判定のサマリ。
 * backend YakurekiEventDetailSerializer で露出されるフィールド。
 */
export type YakurekiDetailTranscription = {
  id: string
  engine: TranscriptionEngine
  status: TranscriptionStatus
  post_batch_status: TranscriptionPostBatchStatus
  post_batch_completed_at: string | null
}

export type YakurekiDetail = {
  id: string
  label: string
  /** 遠隔服薬指導の依頼 (back log/250)。無ければ null */
  remote?: RemoteGuidanceSummary | null
  /**
   * 紐付け患者 ID. NSIPS 経由で取得した event は string, フリー録音 event は null.
   * Phase A フリー録音導入で nullable に拡張 (back 既存 serializers.py:625-628 は
   * nsips/patient null 時に None を返していたが、 type は string 固定だった).
   */
  patient_id: string | null
  /**
   * 患者漢字氏名 (= NSIPS patient.name_kanji snapshot). フリー録音時は null.
   * h1 表示の主役で、 label より優先される (= label は queue_number prefix 等を含む
   * format になっている場合があるため canonical な氏名 field を使う).
   */
  patient_name?: string | null
  /** NSIPS 受付番号. フリー録音時は null. sidebar row で「001 田中」 のように表示用. */
  queue_number?: string | null
  status: YakurekiStatus
  /**
   * 来局(walk_in)/在宅(home_visit)の別。back 既定は walk_in。
   * home_visit のときだけ front は訪問報告書サイドカー (soaps.home_visit_fields 等) を描画する。
   * 旧データ互換のため optional 受け (読み出し側は `?? 'walk_in'` で解決)。
   */
  session_type?: SessionType
  current_prescription_data: PrescriptionData
  previous_prescription_data: PrescriptionData | null
  is_create_from_nsips: boolean
  /**
   * 対象患者の家族 (同伴/代理) が会話に参加した場合 true。
   * 録音 start 時に薬剤師がチェックボックスで指定し、SOAP 生成 prompt の
   * `# 登場する話者の可能性` 振り分け規則テーブルに「対象患者の家族 (同伴/代理)」行を露出させる
   * (yakureki-back log/145/work-log/01)。
   */
  has_companion_or_substitute: boolean
  dictated: Dictated | null
  soaps: Soaps | null
  audio: Audio
  created_at: string
  gender?: PatientGender
  age?: number
  memo_items?: string[]
  pharmacist_id?: string | null
  pharmacist_name?: string | null
  transcription: YakurekiDetailTranscription | null
  /**
   * 一時停止中セッションの復元情報 (log/185 サーバ復元)。paused の正本はサーバ側
   * (SS.status=PAUSED + StreamingSegment) で、 front は本フィールドから [再開]/[完了]/[破棄]
   * UI を復元する。患者切替・reload・ブラウザ再起動・別端末を全て跨げる。paused でなければ null。
   */
  paused_streaming_session?: {
    session_id: string
    next_segment_index: number
    staged_duration_s: number
    yakureki_ids: string[]
    paused_at: string | null
  } | null
  // 録音中 sequential update LLM が抽出する素材 (= SOAP 記述に関わる発話 / 介入レコメンド).
  // 空配列が初期値 (back JSONField default=list)。
  highlights: Highlight[]
  // 「今確認すべきこと」。指導前の確認要点を起点に毎周期 current state を上書き。
  intervention_recommendations: string[]
  /**
   * SOAP 再生成 (regenerate / regenerate-soap action) の累計実行回数.
   * 上限は back services/regenerate/shared.py:MAX_REGENERATE_COUNT (= 3 回).
   * apps/latest PostRecordingView の button gating で残回数表示に使う.
   * back 0101 migration で導入. デフォルト 0.
   */
  regenerate_count?: number
}

// 内部型定義
type Audio = {
  id: string
  audio: string
  // 1 秒ごとの音の大きさ (0〜100)。管理栄養士の記録だけ back が測る。null = 測っていない
  loudness?: number[] | null
}

type Sentence = {
  speaker: string
  sentence: string
}

export type ConversationSegment = {
  speaker: string
  content: string
}

type Dictated = {
  id: string
  sentences: Sentence[]
  conversation_segments: ConversationSegment[]
  conversation_summary: string[]
}
